import { useCallback } from 'react';
import { useStore } from '@tanstack/react-form';
import { NotifyManager } from 'notify-component';
import { getFieldError } from '#app/utils/tanstackFormErrorVisibility';

/**
 * Переиспользуемый хук для работы с серверными ошибками в TanStack Form.
 *
 * Предоставляет:
 * - getError — получить строку ошибки по имени поля (клиентская + серверная)
 * - setServerErrors — установить серверные ошибки по полям
 * - clearServerErrors — сбросить серверные ошибки перед отправкой
 * - showServerNotification — показать нотификацию если ошибка без поля (только message)
 * - hasClientErrors / hasServerErrors — для логики в onSubmit: запрос не уходит при только клиентских ошибках; при наличии серверных — повторная отправка разрешена
 */
export function useTanstackFormErrors(tanstackForm: any) {
  const fieldMeta = useStore(tanstackForm.store, (state: any) => state.fieldMeta);

  const getError = useCallback(
    (fieldName: string): string => {
      const meta = (tanstackForm.state.fieldMeta as Record<string, any>)?.[fieldName];
      return getFieldError(meta?.errorMap ?? {}) ?? '';
    },
    [tanstackForm]
  );

  /**
   * Устанавливает серверные ошибки по полям.
   * errors — объект вида { fieldName: string[] } или { fieldName: string }.
   * Безопасно вызывать с null/undefined — вызов игнорируется.
   */
  const setServerErrors = useCallback(
    (errors: Record<string, string | string[]> | null | undefined) => {
      if (!errors || typeof errors !== 'object') return;
      Object.entries(errors).forEach(([field, messages]) => {
        const msg = Array.isArray(messages) ? messages[0] : messages;
        if (msg) {
          tanstackForm.setFieldMeta(field as any, (prev: any) => ({
            ...(prev ?? {}),
            errorMap: { ...(prev?.errorMap ?? {}), onServer: msg },
          }));
        }
      });
    },
    [tanstackForm]
  );

  /**
   * Сбрасывает серверные ошибки у всех полей формы.
   * Вызывать перед handleSubmit.
   */
  const clearServerErrors = () => {
    const meta = tanstackForm.state.fieldMeta as Record<string, any>;
    if (!meta || typeof meta !== 'object') return;
    Object.keys(meta).forEach((key) => {
      tanstackForm.setFieldMeta(key as any, (prev: any) => ({
        ...(prev ?? {}),
        errorMap: { ...(prev?.errorMap ?? {}), onServer: undefined },
      }));
    });
  };

  /**
   * Показывает нотификацию если сервер вернул только message без полевых ошибок.
   * Использовать в onError мутации.
   */
  const showServerNotification = (error: any) => {
    if (!error?.meta?.errors && error?.meta?.message) {
      NotifyManager.error('Ошибка', error.meta.message, 5000);
    }
  };

  /**
   * true если у формы есть клиентские ошибки (onSubmit/onChange/onBlur).
   * Серверные ошибки (onServer) не учитываются — кнопка не блокируется из-за них.
   */
  const hasClientErrors = (() => {
    if (!fieldMeta) return false;
    return Object.values(fieldMeta as Record<string, any>).some((meta) => {
      const errorMap = meta?.errorMap ?? {};
      return Object.entries(errorMap).some(([key, value]) => key !== 'onServer' && Boolean(value));
    });
  })();

  /**
   * true если у любого поля есть серверная ошибка (onServer).
   * Нужно, чтобы при наличии серверных ошибок разрешать повторную отправку запроса.
   */
  const hasServerErrors = (() => {
    if (!fieldMeta) return false;
    return Object.values(fieldMeta as Record<string, any>).some((meta) => meta?.errorMap?.onServer);
  })();

  return {
    getError,
    setServerErrors,
    clearServerErrors,
    showServerNotification,
    hasClientErrors,
    hasServerErrors,
  };
}
